// Dwell-click service — fires a click on whatever is under the
// cursor after the user holds it still for the configured dwell
// duration. Useful for eye-tracking, head-tracking or users with
// limited hand motion.
//
// A subtle teal progress ring animates around the cursor while the
// dwell timer counts up, giving visual feedback.

const MOVE_TOLERANCE_PX = 10;

let started = false;
let store = null;
let overlay = null;
let ring = null;
let timer = null;
let lastX = 0;
let lastY = 0;
let startedAt = 0;
let targetEl = null;

function createOverlay() {
  overlay = document.createElement('div');
  overlay.className = 'a11y-dwell-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.innerHTML = `
    <svg viewBox="0 0 48 48" width="48" height="48">
      <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(4,120,115,0.2)" stroke-width="4" />
      <circle class="a11y-dwell-overlay__ring" cx="24" cy="24" r="20" fill="none"
              stroke="#047873" stroke-width="4" stroke-linecap="round"
              stroke-dasharray="125.6" stroke-dashoffset="125.6"
              transform="rotate(-90 24 24)" />
    </svg>
  `;
  overlay.style.cssText = [
    'position:fixed',
    'pointer-events:none',
    'width:48px',
    'height:48px',
    'z-index:9999',
    'opacity:0',
    'transition:opacity 120ms',
    'transform:translate(-50%,-50%)'
  ].join(';');
  document.body.appendChild(overlay);
  ring = overlay.querySelector('.a11y-dwell-overlay__ring');
}

function isClickable(el) {
  if (!el || el.nodeType !== 1) return false;
  if (el.disabled) return false;
  // Ignore clicks inside the accessibility panel itself
  if (el.closest && el.closest('.a11y-wrap, .osk')) return false;
  const tag = el.tagName;
  if (['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL'].includes(tag))
    return true;
  const role = el.getAttribute && el.getAttribute('role');
  if (
    role &&
    [
      'button',
      'link',
      'menuitem',
      'tab',
      'option',
      'checkbox',
      'radio'
    ].includes(role)
  )
    return true;
  // Any element with an onClick handler (heuristic via tabindex)
  if (
    el.getAttribute &&
    el.getAttribute('tabindex') !== null &&
    el.getAttribute('tabindex') !== '-1'
  )
    return true;
  return false;
}

function findClickableAncestor(el) {
  let node = el;
  let depth = 0;
  while (node && depth < 5) {
    if (isClickable(node)) return node;
    node = node.parentElement;
    depth++;
  }
  return null;
}

function startDwellTimer() {
  if (!store) return;
  const f = store.getters['Accessibility/getFeatures'];
  const delay = f.dwellDelay || 1200;

  startedAt = Date.now();
  overlay.style.opacity = '1';
  ring.style.transition = `stroke-dashoffset ${delay}ms linear`;
  // Force reflow so the transition starts from the full dashoffset
  // eslint-disable-next-line no-unused-expressions
  ring.getBoundingClientRect();
  ring.style.strokeDashoffset = '0';

  timer = setTimeout(() => {
    if (targetEl && targetEl.isConnected) {
      targetEl.click();
      // Focus inputs so the OSK can type into them
      if (targetEl.focus) {
        try {
          targetEl.focus({ preventScroll: true });
        } catch (e) {
          /* noop */
        }
      }
    }
    resetRing();
  }, delay);
}

function resetRing() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!ring) return;
  ring.style.transition = 'none';
  ring.style.strokeDashoffset = '125.6';
  if (overlay) overlay.style.opacity = '0';
  targetEl = null;
}

function onMouseMove(e) {
  if (!overlay) return;
  overlay.style.left = e.clientX + 'px';
  overlay.style.top = e.clientY + 'px';

  const dx = Math.abs(e.clientX - lastX);
  const dy = Math.abs(e.clientY - lastY);

  // If cursor moved more than tolerance, reset the dwell timer
  if (dx > MOVE_TOLERANCE_PX || dy > MOVE_TOLERANCE_PX) {
    resetRing();
    lastX = e.clientX;
    lastY = e.clientY;
    // Look up the clickable under the (new) cursor position
    const hovered = document.elementFromPoint(e.clientX, e.clientY);
    targetEl = findClickableAncestor(hovered);
    if (targetEl) startDwellTimer();
  }
}

function onMouseLeave() {
  resetRing();
}

export function startDwellClick(vuexStore) {
  if (started) return;
  store = vuexStore;
  createOverlay();
  document.addEventListener('mousemove', onMouseMove, true);
  document.addEventListener('mouseleave', onMouseLeave);
  started = true;
}

export function stopDwellClick() {
  if (!started) return;
  document.removeEventListener('mousemove', onMouseMove, true);
  document.removeEventListener('mouseleave', onMouseLeave);
  resetRing();
  if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
  overlay = null;
  ring = null;
  started = false;
}
